import { useEffect, useState, type FormEvent } from "react";
import {
  acceptCustomerQuotation,
  getCustomerQuotation,
  getCustomerQuotations,
  type Customer,
} from "./api.js";
type Summary = {
  quotationPublicId: string;
  status: string;
  totalAmountMinor: string;
  expiresAt: string;
};
type Detail = Summary & {
  snapshot: {
    quotationVersionPublicId: string;
    contentHash: string;
    terms: { en: string; ar: string };
    lines: Array<{
      quantity: number;
      productContent: { en: { title: string }; ar: { title: string } };
    }>;
  };
  acceptance: { status: string; orderPublicId: string | null } | null;
};
export function CustomerQuotations({
  customer,
  ar,
  quotationId,
  navigate,
}: {
  customer: Customer;
  ar: boolean;
  quotationId?: string | undefined;
  navigate(path: string): void;
}) {
  const [items, setItems] = useState<Summary[]>([]);
  const [quote, setQuote] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true),
    [pending, setPending] = useState(false);
  const [error, setError] = useState(""),
    [mode, setMode] = useState("PICKUP");
  const [key, setKey] = useState(() => crypto.randomUUID());
  const load = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      if (quotationId)
        setQuote((await getCustomerQuotation(quotationId)) as Detail);
      else setItems((await getCustomerQuotations()) as Summary[]);
      setError("");
    } catch {
      setError(
        ar
          ? "تعذر تحميل عروض الأسعار. أعد المحاولة."
          : "Could not load quotations. Please retry.",
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setQuote(null);
    setKey(crypto.randomUUID());
    void load();
  }, [quotationId]);
  useEffect(() => {
    if (quote?.acceptance?.status !== "PENDING") return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void load(true);
    }, 10000);
    return () => window.clearInterval(timer);
  }, [quotationId, quote?.acceptance?.status]);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!quote || !quotationId || pending) return;
    const form = new FormData(event.currentTarget);
    setPending(true);
    setError("");
    try {
      await acceptCustomerQuotation(
        quotationId,
        {
          quotationVersionPublicId: quote.snapshot.quotationVersionPublicId,
          expectedContentHash: quote.snapshot.contentHash,
          contact: { name: form.get("name"), email: customer.email },
          delivery:
            mode === "PICKUP"
              ? { mode }
              : {
                  mode,
                  address: { line1: form.get("line1"), city: form.get("city") },
                },
          paymentMethod: form.get("paymentMethod"),
        },
        key,
      );
      await load();
    } catch {
      setError(
        ar
          ? "تعذر قبول العرض. حدّث الحالة للتحقق من الصلاحية والإصدار."
          : "Could not accept. Refresh the quotation to check its validity and current version.",
      );
    } finally {
      setPending(false);
    }
  };
  const money = (minor: string) =>
    new Intl.NumberFormat(ar ? "ar-EG" : "en-EG", {
      style: "currency",
      currency: "EGP",
    }).format(Number(minor) / 100);
  return (
    <section className="account-dashboard" aria-busy={loading}>
      <div className="account-content__heading">
        <h2>{ar ? "عروض الأسعار" : "Quotations"}</h2>
        <button
          className="button secondary"
          disabled={loading || pending}
          onClick={() => void load()}
        >
          {ar ? "تحديث" : "Refresh"}
        </button>
      </div>
      {error ? (
        <p role="alert" className="error-summary">
          {error}
        </p>
      ) : null}
      {loading ? (
        <p role="status">{ar ? "جارٍ التحميل…" : "Loading…"}</p>
      ) : quotationId && quote ? (
        <>
          <button
            className="button secondary"
            onClick={() => navigate("/account/quotations")}
          >
            {ar ? "كل العروض" : "All quotations"}
          </button>
          <h3>
            {quote.snapshot.lines[0]?.productContent[ar ? "ar" : "en"].title}
          </h3>
          <p>
            {quote.quotationPublicId} · {quote.status}
          </p>
          <p>
            {ar ? "الإجمالي" : "Total"}:{" "}
            <strong>{money(quote.totalAmountMinor)}</strong>
          </p>
          <p>
            {ar ? "صالح حتى" : "Valid until"}:{" "}
            {new Date(quote.expiresAt).toLocaleString(ar ? "ar-EG" : "en-EG")}
          </p>
          <p style={{ whiteSpace: "pre-wrap" }}>
            {quote.snapshot.terms[ar ? "ar" : "en"]}
          </p>
          {quote.acceptance?.status === "PENDING" ? (
            <p role="status">
              {ar
                ? "تم تسجيل موافقتك. ننتظر تأكيد نظام الإنتاج؛ لا تحتاج لتكرار الطلب."
                : "Your acceptance is recorded. Waiting for the production system to confirm; you do not need to submit again."}
            </p>
          ) : null}
          {quote.acceptance?.orderPublicId ? (
            <button
              className="button primary"
              onClick={() =>
                navigate("/account/orders/" + quote.acceptance!.orderPublicId)
              }
            >
              {ar ? "تتبع الطلب" : "Track your order"}
            </button>
          ) : null}
          {quote.acceptance &&
          !["PENDING", "ACCEPTED"].includes(quote.acceptance.status) ? (
            <p role="status">
              {ar
                ? "لم يتحول العرض إلى طلب. تواصل معنا للحصول على عرض صالح."
                : "This acceptance did not create an order. Contact us for a current quotation."}{" "}
              ({quote.acceptance.status})
            </p>
          ) : null}
          {quote.status === "ISSUED" &&
          !["PENDING", "ACCEPTED"].includes(quote.acceptance?.status ?? "") ? (
            <form
              className="form-stack"
              onSubmit={submit}
              onChange={() => setKey(crypto.randomUUID())}
            >
              <label>
                {ar ? "الاسم" : "Name"}
                <input
                  name="name"
                  defaultValue={customer.firstName + " " + customer.lastName}
                  maxLength={100}
                  required
                />
              </label>
              <label>
                {ar ? "طريقة الاستلام" : "Delivery"}
                <select value={mode} onChange={(e) => setMode(e.target.value)}>
                  <option value="PICKUP">
                    {ar ? "استلام من زويا القاهرة" : "Pickup at Zoya Cairo"}
                  </option>
                  <option value="COURIER">{ar ? "شحن" : "Courier"}</option>
                </select>
              </label>
              {mode === "COURIER" ? (
                <>
                  <label>
                    {ar ? "العنوان" : "Address"}
                    <input name="line1" required maxLength={160} />
                  </label>
                  <label>
                    {ar ? "المدينة" : "City"}
                    <input name="city" required maxLength={100} />
                  </label>
                </>
              ) : null}
              <label>
                {ar ? "طريقة الدفع" : "Payment method"}
                <select name="paymentMethod">
                  <option value="CASH">{ar ? "نقدي" : "Cash"}</option>
                  <option value="COD">
                    {ar ? "الدفع عند الاستلام" : "Cash on delivery"}
                  </option>
                  <option value="BANK_TRANSFER">
                    {ar ? "تحويل بنكي" : "Bank transfer"}
                  </option>
                  <option value="INSTAPAY">InstaPay</option>
                </select>
              </label>
              <label>
                <input type="checkbox" required />
                {ar
                  ? "أوافق على السعر والشروط لهذا الإصدار من العرض."
                  : "I accept the price and terms of this quotation version."}
              </label>
              <button
                type="submit"
                className="button primary"
                disabled={pending}
              >
                {pending
                  ? ar
                    ? "جارٍ التسجيل…"
                    : "Submitting…"
                  : ar
                    ? "قبول العرض وطلب التنفيذ"
                    : "Accept quotation & place order"}
              </button>
            </form>
          ) : null}
        </>
      ) : !items.length ? (
        <p>
          {ar
            ? "لا توجد عروض أسعار بعد. ستظهر هنا بعد مراجعة طلبك."
            : "No quotations yet. They will appear here after your request is reviewed."}
        </p>
      ) : (
        <div className="account-order-list">
          {items.map((item) => (
            <article
              className="account-order-card"
              key={item.quotationPublicId}
            >
              <h3>{item.quotationPublicId}</h3>
              <p>
                {item.status} · {money(item.totalAmountMinor)}
              </p>
              <button
                className="button secondary"
                onClick={() =>
                  navigate("/account/quotations/" + item.quotationPublicId)
                }
              >
                {ar ? "مراجعة العرض" : "Review quotation"}
              </button>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
